import i18next from 'i18next'
import { Set } from 'immutable'
import React from 'react'
import { Link } from 'widgets-for-react'
import { categoryIconId } from '../../../../utils/categories'
import { SVGIcon } from '../../../../utils/svgicon'
import { slugify } from '../../../../utils/utils'
import { ViewListenerState } from '../../viewListener/viewListenerState'
import { MainCategory } from '../filtersLoader/filtersLoaderState'
import { filteredUrl, TopFilters } from '../filtersUtils'
import { CategoriesSelection } from './categoriesFilterUtils'

export const CategoriesFilterLayout = (props: {
  topFilters: TopFilters
  selectedCategories: CategoriesSelection
  categories: MainCategory[]
  viewListenerState: ViewListenerState
  expandedCategories: Set<string>
  updateExpandedCategories: (_: Set<string>) => void
}) => {
  const {
    topFilters,
    categories,
    expandedCategories,
    selectedCategories,
    updateExpandedCategories,
    viewListenerState,
  } = props

  const {
    category: selectedCategory,
    subcategory: selectedSubcategory,
    ingredient: selectedIngredient,
  } = selectedCategories

  return (
    <section className="c-filter__sidebar side-filter">
      <h2 className="sr-text">{i18next.t('component:categoryFiltersTitle')}</h2>

      {viewListenerState === 'mobile' ? (
        // Mobile
        <>
          <nav
            className="side-filter__breadcrumb breadcrumb"
            aria-label="Category filter"
          >
            <ol
              className={`breadcrumb__list ${
                selectedCategory !== undefined &&
                selectedSubcategory !== undefined
                  ? 'breadcrumb__list--3'
                  : ''
              } text--s`}
            >
              <li
                key="breadcrumb-all"
                className={`breadcrumb__item ${
                  selectedCategory !== undefined ? 'text--primary' : ''
                }`}
              >
                {selectedCategory !== undefined ? (
                  <Link
                    to={filteredUrl(
                      `/${
                        i18next.language === 'nl'
                          ? 'nl/componenten'
                          : 'en/components'
                      }`
                    )({ topFilters: topFilters })}
                    className="breadcrumb__link link--inverted"
                    onClick={scrollToStart}
                  >
                    {i18next.t('componentFilter:allComponents')}
                  </Link>
                ) : (
                  <span className="weight--semibold">
                    {i18next.t('componentFilter:allComponents')}
                  </span>
                )}
              </li>

              {selectedCategory !== undefined && (
                <li
                  key="breadcrumb-category"
                  className={`breadcrumb__item ${
                    selectedCategory !== undefined &&
                    selectedSubcategory !== undefined
                      ? 'text--primary'
                      : ''
                  }`}
                >
                  {selectedCategory !== undefined &&
                  selectedSubcategory !== undefined ? (
                    <Link
                      to={filteredUrl(
                        `/${
                          i18next.language === 'nl'
                            ? 'nl/componenten'
                            : 'en/components'
                        }`
                      )({
                        categoriesFilter: { category: selectedCategory.Title },
                        topFilters: topFilters,
                      })}
                      className="breadcrumb__link link--inverted"
                    >
                      {selectedCategory.Translation}
                    </Link>
                  ) : (
                    <span className="weight--semibold">
                      {selectedCategory.Translation}
                    </span>
                  )}
                </li>
              )}
              {selectedCategory !== undefined &&
                selectedSubcategory !== undefined && (
                  <li className="breadcrumb__item" key="breadcrumb-subcategory">
                    <span className="weight--semibold">
                      {selectedSubcategory.Translation}
                    </span>
                  </li>
                )}
            </ol>
          </nav>

          <ul className="side-filter__categories">
            {selectedCategory === undefined
              ? categories.map((category) => (
                  <li className="side-filter__category" key={category.Title}>
                    <LinkToCategory
                      categoryUrl={filteredUrl(
                        `/${
                          i18next.language === 'nl'
                            ? 'nl/componenten'
                            : 'en/components'
                        }`
                      )({
                        categoriesFilter: { category: category.Title },
                        topFilters: topFilters,
                      })}
                      isMobile={true}
                      isExpanded={true}
                      isLink={true}
                      isSelected={false}
                      CategoryTitle={category.Title}
                      CategoryName={category.Translation}
                    />
                  </li>
                ))
              : selectedSubcategory === undefined
              ? selectedCategory.Subcategories.map((subcategory) => (
                  <li
                    className="side-filter__subcategory"
                    key={subcategory.Title}
                  >
                    <LinkToSubcategory
                      topFilters={topFilters}
                      isMobile={true}
                      isSelected={false}
                      CategoryTitle={selectedCategory.Title}
                      SubcategoryTitle={subcategory.Title}
                      SubcategoryName={subcategory.Translation}
                    />
                  </li>
                ))
              : selectedSubcategory.Ingredients.map((ingredient) => {
                  const isIngredientSelected =
                    selectedIngredient?.Title == ingredient.Title
                  return (
                    <li
                      className="side-filter__ingredient"
                      key={ingredient.Title}
                    >
                      <LinkToIngredient
                        className="button--ghost button--s side-filter__clickable side-filter__clickable--ingredient"
                        ingredientUrl={filteredUrl(
                          `/${
                            i18next.language === 'nl'
                              ? 'nl/componenten'
                              : 'en/components'
                          }`
                        )({
                          categoriesFilter: {
                            category: selectedCategory.Title,
                            subcategory: selectedSubcategory.Title,
                            ingredient: ingredient.Title,
                          },
                          topFilters: topFilters,
                        })}
                        isSelected={isIngredientSelected}
                        IngredientName={ingredient.Translation}
                      />
                    </li>
                  )
                })}
          </ul>
        </>
      ) : (
        // Desktop
        <>
          <MaybeLink
            to={
              selectedCategory === undefined
                ? undefined
                : filteredUrl(
                    `/${
                      i18next.language === 'nl'
                        ? 'nl/componenten'
                        : 'en/components'
                    }`
                  )({ topFilters: topFilters })
            }
            className="button--secondary button--m side-filter__clickable side-filter__clickable--all"
            title={i18next.t('componentFilter:viewAllComponents')}
          >
            <svg
              className="button__icon"
              width="22px"
              height="22px"
              aria-hidden="true"
            >
              <use xlinkHref="#icon--hexagon-sliced-closed-bold" />
            </svg>
            <span className="button__label">
              {i18next.t('componentFilter:allComponents')}
            </span>
          </MaybeLink>

          <ul className="side-filter__categories">
            {categories.map((category) => {
              const categoryIsSelected =
                selectedCategory?.Title == category.Title
              const categoryIsExpanded = expandedCategories.includes(
                category.Title
              )
              return (
                <li className="side-filter__category" key={category.Title}>
                  <LinkToCategory
                    categoryUrl={filteredUrl(
                      `/${
                        i18next.language === 'nl'
                          ? 'nl/componenten'
                          : 'en/components'
                      }`
                    )({
                      categoriesFilter: { category: category.Title },
                      topFilters: topFilters,
                    })}
                    isMobile={false}
                    isLink={
                      !categoryIsSelected || selectedSubcategory != undefined
                    }
                    isSelected={categoryIsSelected}
                    isExpanded={categoryIsExpanded}
                    CategoryTitle={category.Title}
                    CategoryName={category.Translation}
                  />
                  <button
                    type="button"
                    className="button__chevron"
                    title={i18next.t('componentFilter:toggleCategory', {
                      toggle: i18next.t(
                        `common:${categoryIsExpanded ? 'close' : 'open'}`
                      ),
                    })}
                    onClick={() =>
                      updateExpandedCategories(
                        categoryIsExpanded
                          ? expandedCategories.remove(category.Title)
                          : expandedCategories.add(category.Title)
                      )
                    }
                  >
                    <span className="sr-text">
                      {i18next.t('componentFilter:toggleCategory', {
                        toggle: i18next.t(
                          `common:${categoryIsExpanded ? 'close' : 'open'}`
                        ),
                      })}
                    </span>
                    <SVGIcon size={10} iconId="chevron-down" />
                  </button>

                  {/* Sub-categories */}
                  <ul
                    className="side-filter__subcategories"
                    id={`a11y-subcat-${slugify(category.Title)}`}
                    aria-hidden={!categoryIsExpanded}
                  >
                    {category.Subcategories.map((subcategory) => {
                      const subcategoryIsSelected =
                        selectedSubcategory?.Title == subcategory.Title
                      return (
                        <li
                          className="side-filter__subcategory"
                          key={subcategory.Title}
                        >
                          <LinkToSubcategory
                            topFilters={topFilters}
                            isMobile={false}
                            isSelected={
                              subcategoryIsSelected &&
                              selectedCategories.ingredient == undefined
                            }
                            CategoryTitle={category.Title}
                            SubcategoryTitle={subcategory.Title}
                            SubcategoryName={subcategory.Translation}
                          />

                          {/* Ingredients */}
                          <ul
                            className="side-filter__ingredients"
                            id={`a11y-mainingr-${slugify(subcategory.Title)}`}
                            aria-hidden={!subcategoryIsSelected}
                          >
                            {subcategory.Ingredients.map((ingredient) => {
                              const ingredientIsSelected =
                                selectedIngredient?.Title === ingredient.Title
                              return (
                                <li
                                  key={ingredient.Title}
                                  className="side-filter__ingredient"
                                >
                                  <LinkToIngredient
                                    className={
                                      ingredientIsSelected
                                        ? 'text--primary weight--semibold side-filter__clickable side-filter__clickable--ingredient'
                                        : 'link--styled-grey text--s side-filter__clickable side-filter__clickable--ingredient'
                                    }
                                    ingredientUrl={filteredUrl(
                                      `/${
                                        i18next.language === 'nl'
                                          ? 'nl/componenten'
                                          : 'en/components'
                                      }`
                                    )({
                                      categoriesFilter: {
                                        category: category.Title,
                                        subcategory: subcategory.Title,
                                        ingredient: ingredient.Title,
                                      },
                                      topFilters: topFilters,
                                    })}
                                    isSelected={ingredientIsSelected}
                                    IngredientName={ingredient.Translation}
                                  />
                                </li>
                              )
                            })}
                          </ul>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </section>
  )
}

const MaybeLink = (props: {
  to?: string
  className: string
  title?: string
  children: React.ReactNode
}) =>
  props.to === undefined ? (
    <span
      className={props.className}
      title={props.title}
      aria-current="location"
    >
      {props.children}
    </span>
  ) : (
    <Link to={props.to} className={props.className} title={props.title}>
      {props.children}
    </Link>
  )

const LinkToCategory = (props: {
  categoryUrl: string
  isMobile: boolean
  isSelected: boolean
  isExpanded: boolean
  isLink: boolean
  CategoryTitle: string
  CategoryName: string
}) => {
  const {
    categoryUrl,
    CategoryTitle,
    CategoryName,
    isExpanded,
    isMobile,
    isSelected,
    isLink,
  } = props
  const actionProps = {
    className: !isMobile
      ? `button--secondary button--m side-filter__clickable side-filter__clickable--category ${
          isSelected ? 'side-filter__clickable--active' : ''
        }`
      : 'button--secondary button--s side-filter__clickable side-filter__clickable--category',
    'aria-expanded': !isMobile ? isExpanded : false,
    'aria-controls': `a11y-subcat-${slugify(CategoryTitle)}`,
  }
  const ActionInnerLayout = (
    <>
      <svg
        className="button__icon"
        width="22px"
        height="22px"
        aria-hidden="true"
      >
        <use xlinkHref={`#icon--${categoryIconId(CategoryTitle)}`} />
      </svg>
      <span className="button__label">{CategoryName}</span>
    </>
  )
  return (
    <>
      {!isLink ? (
        <div aria-current="location" {...actionProps}>
          {ActionInnerLayout}
        </div>
      ) : (
        <Link
          to={categoryUrl}
          aria-current={isSelected ? 'location' : undefined}
          onClick={scrollToStart}
          {...actionProps}
        >
          {ActionInnerLayout}
        </Link>
      )}
    </>
  )
}

const LinkToSubcategory = (props: {
  topFilters: TopFilters
  isMobile: boolean
  isSelected: boolean
  CategoryTitle: string
  SubcategoryTitle: string
  SubcategoryName: string
}) => {
  const {
    CategoryTitle,
    SubcategoryName,
    SubcategoryTitle,
    isMobile,
    isSelected,
  } = props
  const actionProps = {
    className: !isMobile
      ? isSelected
        ? 'text--primary weight--semibold side-filter__clickable side-filter__clickable--subcategory'
        : 'link--styled-grey text--s side-filter__clickable side-filter__clickable--subcategory'
      : 'button--ghost button--s side-filter__clickable side-filter__clickable--subcategory',
    'aria-expanded': isSelected,
    'aria-controls': `a11y-mainingr-${slugify(SubcategoryTitle)}`,
  }
  const ActionInnerLayout = (
    <>
      <span className="link__label">{SubcategoryName}</span>
      {!isSelected && (
        <span className="sr-text">
          {i18next.t('componentFilter:toggleSubcategory', {
            toggle: i18next.t('common:open'),
          })}
        </span>
      )}
    </>
  )
  return isSelected ? (
    <span aria-current="location" {...actionProps}>
      {ActionInnerLayout}
    </span>
  ) : (
    <Link
      to={filteredUrl(
        `/${i18next.language === 'nl' ? 'nl/componenten' : 'en/components'}`
      )({
        categoriesFilter: {
          category: CategoryTitle,
          subcategory: SubcategoryTitle,
        },
        topFilters: props.topFilters,
      })}
      onClick={scrollToStart}
      {...actionProps}
    >
      {ActionInnerLayout}
    </Link>
  )
}

const LinkToIngredient = (props: {
  ingredientUrl: string
  className: string
  isSelected: boolean
  IngredientName: string
}) =>
  props.isSelected ? (
    <span aria-current="location" className={props.className}>
      <span className="link__label">{props.IngredientName}</span>
    </span>
  ) : (
    <Link to={props.ingredientUrl} className={props.className}>
      <span className="link__label">{props.IngredientName}</span>
    </Link>
  )

const scrollToStart = () =>
  document.querySelector('.side-filter__categories')?.scrollTo(0, 0)
